import Component from '@glimmer/component';
import { isPresent } from '@ember/utils';
import { cached } from '@glimmer/tracking';
import { GetIntervalCumulativeAmounts } from 'donor-elf-web/lib/journal-utils';
import dateUtils from 'donor-elf-web/lib/date-utils';
import { service } from '@ember/service';
import JournalInfo from 'donor-elf-web/lib/models/journal-info';
import JournalDashboardModel from 'donor-elf-web/models/journal-dashboard';
import AbilitiesService from 'donor-elf-web/services/abilities';
import CurrentUser from 'donor-elf-web/services/current-user';
import JournalContactInfo from 'donor-elf-web/lib/models/journal-contact-info';

export interface ComponentArgs {
  journalInfo: JournalInfo;
  journalContactInfos: JournalContactInfo[];
  journalDashboard: JournalDashboardModel;
}

interface YesDecision {
  amount: number;
  decisionDate: string; // '2021-04-02'
  firstGiftDate: string; // '2021-04-02'
}

// TODO: replace most of the methods below with { JournalCalculations } from 'donor-elf-web/lib/journal-utils'

export default class JournalDashboardComponent extends Component<ComponentArgs> {
  @service declare abilities: AbilitiesService;
  @service declare currentUser: CurrentUser;

  get isUniversityChristianOutreachUser() {
    return this.currentUser.organizationId === '239';
  }

  get isIntervarsityUser() {
    return this.abilities.hasAbility('import_iv_prospects');
  }

  get decisionsMadeLabel() {
    return this.isIntervarsityUser ? 'Journal Pledges' : 'Decisions Made';
  }

  get decisionsRequireGift() {
    return this.args.journalInfo?.journal?.decisionsRequireGift;
  }

  get goal() {
    return this.args.journalInfo?.goals[0];
  }

  get goalAmount() {
    return this.goal?.amount || 1;
  }

  get journalGoalDate() {
    return this.args.journalInfo?.journal?.goalDate;
  }

  get chartHeight() {
    return 125;
  }

  @cached
  get raisedAmount() {
    return this.decisionsRequireGift
      ? this.withGiftAmount
      : this.decisionAmount;
  }

  get raisedPercent() {
    return Math.round((this.raisedAmount * 100) / this.goalAmount);
  }

  @cached
  get decisionAmount() {
    let { intervalCumulativeAmounts } = this;
    return (
      intervalCumulativeAmounts[intervalCumulativeAmounts.length - 1]
        ?.decision || 0
    );
  }

  get decisionPercent() {
    return (
      Math.round((this.decisionAmount * 100) / this.goalAmount) -
      this.withGiftPercent
    );
  }

  @cached
  get withGiftAmount() {
    let { intervalCumulativeAmounts } = this;
    return (
      intervalCumulativeAmounts[intervalCumulativeAmounts.length - 1]
        ?.withGift || 0
    );
  }

  get withGiftPercent() {
    return Math.round((this.withGiftAmount * 100) / this.goalAmount);
  }

  // Array<YesDecision> [{ amount: 5.5, decisionDate: '2021-04-02', firstGiftDate: '2021-04-02' }]
  get yesDecisions(): YesDecision[] {
    let { goal } = this;
    if (goal == null) {
      return [];
    }
    let decisions: YesDecision[] = [];
    this.args.journalContactInfos.forEach((info) => {
      info.journalDecisions.forEach((decision) => {
        if (decision.yesNo && decision.journalGoalId === goal.id) {
          decisions.push({
            amount: decision.amount!,
            decisionDate: dateUtils.serializeToDateOnly(decision.decisionDate),
            firstGiftDate: isPresent(decision.firstGiftDate)
              ? dateUtils.serializeToDateOnly(decision.firstGiftDate)
              : null,
          });
        }
      });
    });
    return decisions;
  }

  @cached
  get intervalCumulativeAmounts() {
    if (this.goal == null) {
      return [];
    }
    let {
      yesDecisions,
      args: {
        journalDashboard: { intervalDates },
      },
    } = this;
    return GetIntervalCumulativeAmounts(intervalDates, yesDecisions);
  }
}
